import React, { forwardRef } from 'react';
import {
  Input,
  InputGroup,
  InputProps,
} from '@chakra-ui/react';

interface DatePickerProps extends Omit<InputProps, 'onChange'> {
  selectedDate?: Date;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  maxDate?: Date;
  minDate?: Date;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      selectedDate,
      onChange,
      placeholder = 'Seleccionar fecha',
      maxDate,
      minDate,
      ...props
    },
    ref
  ) => {
    const formatDateForInput = (date?: Date): string => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!value) {
        onChange(null);
        return;
      }

      const [year, month, day] = value.split('-').map(Number);
      const newDate = new Date(year, month - 1, day);

      if (minDate && newDate < minDate) return;
      if (maxDate && newDate > maxDate) return;

      onChange(newDate);
    };

    const minDateAttr = minDate ? formatDateForInput(minDate) : undefined;
    const maxDateAttr = maxDate ? formatDateForInput(maxDate) : undefined;

    return (
      <InputGroup>
        <Input
          ref={ref} 
          type="date"
          value={formatDateForInput(selectedDate)}
          onChange={handleChange}
          placeholder={placeholder}
          min={minDateAttr}
          max={maxDateAttr}
          color="white"
          bg="primary.900"
          borderColor="#7E7D7D"
          _hover={{ borderColor: '#7E7D7D' }}
          _focus={{ borderColor: '#7E7D7D', boxShadow: 'none' }}
          onKeyDown={(e) => e.preventDefault()}
          sx={{
            '::-webkit-calendar-picker-indicator': {
              filter: 'invert(1)',
            },
          }}
          {...props}
        />
      </InputGroup>
    );
  }
);

DatePicker.displayName = 'DatePicker';
